import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Typography
} from 'antd';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import { showError, showSuccess } from '@components/notification';
import { apiGetDevicesInformationV2 } from '@services/ManageDevices/DeviceServices';
import {
  apiAssignSimDevice,
  apiGetSimAssignmentHistory,
  apiUnassignSimDevice
} from '@services/ManageSim/SimServices';

const primaryButtonStyle = {
  height: 36,
  color: '#fff',
  backgroundColor: '#0284c7',
  borderColor: '#0284c7'
};

const SimAssignmentModal = ({ open, record, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [devices, setDevices] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const currentAssignment = record?.current_assignment;

  const fetchDevices = async (keyword) => {
    try {
      setDeviceLoading(true);
      const response = await apiGetDevicesInformationV2({
        keyWord: keyword || undefined,
        page: 1,
        size: 100
      });
      const fetchedDevices = response.data?.data || [];
      const currentDevice = currentAssignment?.device;
      const mergedDevices = currentDevice ? [currentDevice, ...fetchedDevices] : fetchedDevices;
      setDevices(Array.from(new Map(mergedDevices.map((device) => [device.id, device])).values()));
    } catch (error) {
      setDevices([]);
    } finally {
      setDeviceLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!record?.id) return;
    try {
      setHistoryLoading(true);
      const response = await apiGetSimAssignmentHistory(record.id);
      setHistory(response.data?.data || []);
    } catch (error) {
      showError('Không thể tải lịch sử gán SIM.');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchDevices();
    fetchHistory();
    form.setFieldsValue({
      device_id: currentAssignment?.device_id,
      start_date: dayjs(),
      notes: ''
    });
  }, [open, record]);

  const searchDevices = useMemo(() => debounce(fetchDevices, 350), [currentAssignment?.device_id]);
  useEffect(() => () => searchDevices.cancel(), [searchDevices]);

  const handleAssign = async (values) => {
    try {
      setSaving(true);
      await apiAssignSimDevice(record.id, {
        device_id: values.device_id,
        start_date: values.start_date?.toISOString(),
        notes: values.notes
      });
      showSuccess({
        message: currentAssignment
          ? 'Chuyển SIM sang thiết bị mới thành công.'
          : 'Gán SIM với thiết bị thành công.'
      });
      onSuccess();
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể gán SIM với thiết bị.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = () => {
    Modal.confirm({
      title: 'Tháo SIM khỏi thiết bị',
      content: `Tháo SIM khỏi thiết bị ${currentAssignment?.device?.serial_no}?`,
      okText: 'Tháo SIM',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiUnassignSimDevice(record.id, { end_date: new Date().toISOString() });
          showSuccess({ message: 'Tháo SIM khỏi thiết bị thành công.' });
          onSuccess();
        } catch (error) {
          showError(error?.response?.data?.message || 'Không thể tháo SIM khỏi thiết bị.');
        }
      }
    });
  };

  const historyColumns = [
    {
      title: 'Thiết bị',
      dataIndex: 'device',
      render: (device) => device?.serial_no || device?.imei || '-'
    },
    {
      title: 'Từ ngày',
      dataIndex: 'start_date',
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-')
    },
    {
      title: 'Đến ngày',
      dataIndex: 'end_date',
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : 'Hiện tại')
    },
    { title: 'Ghi chú', dataIndex: 'notes', render: (value) => value || '-' }
  ];

  return (
    <Modal
      width={900}
      title="Gán SIM với thiết bị"
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose>
      <Typography.Paragraph>
        ICICD: <Typography.Text strong>{record?.iccid}</Typography.Text>
        {' | '}
        Thiết bị hiện tại:{' '}
        <Typography.Text strong>
          {currentAssignment?.device?.serial_no || 'Chưa gắn'}
        </Typography.Text>
      </Typography.Paragraph>

      <Form form={form} layout="vertical" onFinish={handleAssign}>
        <Form.Item
          label={currentAssignment ? 'Thiết bị mới' : 'Thiết bị'}
          name="device_id"
          rules={[{ required: true, message: 'Vui lòng chọn thiết bị.' }]}>
          <Select
            showSearch
            filterOption={false}
            placeholder="Tìm theo serial hoặc IMEI"
            notFoundContent={deviceLoading ? <Spin size="small" /> : null}
            onSearch={searchDevices}
            options={devices.map((device) => ({
              value: device.id,
              label: `${device.serial_no || device.device_name} - ${device.imei || 'Chưa có IMEI'}`
            }))}
            style={{ height: 36 }}
          />
        </Form.Item>
        <Form.Item
          label="Ngày hiệu lực"
          name="start_date"
          rules={[{ required: true, message: 'Vui lòng chọn ngày hiệu lực.' }]}>
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            disabledDate={(date) => date && date.isAfter(dayjs(), 'day')}
            style={{ width: '100%', height: 36 }}
          />
        </Form.Item>
        <Form.Item label="Ghi chú lần gán" name="notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving} style={primaryButtonStyle}>
            {currentAssignment ? 'Chuyển thiết bị' : 'Gán thiết bị'}
          </Button>
          {currentAssignment && (
            <Button danger onClick={handleUnassign}>
              Tháo SIM
            </Button>
          )}
        </Space>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        Lịch sử gán thiết bị
      </Typography.Title>
      <Table
        size="small"
        rowKey="id"
        loading={historyLoading}
        dataSource={history}
        columns={historyColumns}
        pagination={false}
        scroll={{ x: 700 }}
      />
    </Modal>
  );
};

export default SimAssignmentModal;
